/**
 * Resume Workout Modal
 * Shows when a saved in-progress workout is detected
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useWorkoutStore } from '@/stores/workoutStore'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DumbbellIcon, ClockIcon, CloseIcon } from '@/components/ui/Icons'

interface ResumeWorkoutModalProps {
  onClose?: () => void
}

export function ResumeWorkoutModal({ onClose }: ResumeWorkoutModalProps) {
  const navigate = useNavigate()
  const [isVisible, setIsVisible] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const {
    savedWorkoutInfo,
    checkForSavedWorkout,
    resumeWorkout,
    discardSavedWorkout,
  } = useWorkoutStore()

  useEffect(() => {
    // Check for saved workout on mount
    checkForSavedWorkout().then((info) => {
      if (info) {
        // Small delay for animation
        setTimeout(() => setIsVisible(true), 100)
      }
    })
  }, [checkForSavedWorkout])

  const handleResume = async () => {
    setIsLoading(true)
    const success = await resumeWorkout()
    if (success) {
      navigate('/workout?resumed=true')
    }
    setIsLoading(false)
    setIsVisible(false)
    onClose?.()
  }

  const handleDiscard = async () => {
    await discardSavedWorkout()
    setIsVisible(false)
    onClose?.()
  }

  if (!savedWorkoutInfo) return null

  const timeAgo = getTimeAgo(savedWorkoutInfo.startTime)
  const elapsedTime = getElapsedTime(savedWorkoutInfo.startTime)

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300',
          isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={handleDiscard}
      />

      {/* Modal */}
      <div
        className={cn(
          'fixed inset-x-4 top-1/2 z-50 -translate-y-1/2 max-w-md mx-auto',
          'transition-all duration-300 ease-out',
          isVisible
            ? 'opacity-100 scale-100 translate-y-[-50%]'
            : 'opacity-0 scale-95 translate-y-[-45%] pointer-events-none'
        )}
      >
        <Card variant="elevated" padding="lg" className="relative">
          {/* Close button */}
          <button
            onClick={handleDiscard}
            className="absolute top-3 right-3 p-2 text-text-muted hover:text-text-primary rounded-lg hover:bg-elevated transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
              <DumbbellIcon className="w-8 h-8 text-primary" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-text-primary text-center mb-2">
            Resume Workout?
          </h2>
          <p className="text-sm text-text-muted text-center mb-4">
            You have an unfinished workout
          </p>

          {/* Workout Info Card */}
          <div className="bg-elevated rounded-xl p-4 mb-6">
            <h3 className="font-semibold text-text-primary mb-1">
              {savedWorkoutInfo.displayName}
            </h3>
            <p className="text-sm text-text-muted mb-3">
              Week {savedWorkoutInfo.week} • Phase {savedWorkoutInfo.phase}
            </p>

            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1.5 text-text-secondary">
                <ClockIcon className="w-4 h-4" />
                <span>Started {timeAgo}</span>
              </div>
              <div className="text-text-muted">
                {elapsedTime} elapsed
              </div>
            </div>

            <div className="mt-3 pt-3 border-t border-border">
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-muted">Progress</span>
                <span className="font-medium text-text-primary">
                  {savedWorkoutInfo.exercisesCompleted}/{savedWorkoutInfo.totalExercises} exercises
                </span>
              </div>
              <div className="mt-2 h-2 bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-300"
                  style={{
                    width: `${(savedWorkoutInfo.exercisesCompleted / savedWorkoutInfo.totalExercises) * 100}%`,
                  }}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={handleDiscard}
              disabled={isLoading}
            >
              Discard
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleResume}
              loading={isLoading}
            >
              Resume
            </Button>
          </div>
        </Card>
      </div>
    </>
  )
}

// Helper to format time ago
function getTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return 'over a day ago'
}

// Helper to format elapsed time
function getElapsedTime(startTime: number): string {
  const totalSeconds = Math.floor((Date.now() - startTime) / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

export default ResumeWorkoutModal
